<template>
  <NoteWithIcon v-if="isVisible" color="blue" size="small" class="meta-image-fit-notice">
    <template #text>
      <div>
        <strong>Note:</strong> Images will be {{ currentActionVerb }} slightly to be fit Meta's {{ META_FEED_MIN_ASPECT_RATIO_LABEL }} aspect ratio requirements.
      </div>
      <div>
        You can
        <LinkWithIcon class="meta-image-fit-notice__toggle" isDottedUnderline color="blue" :isDisabled="props.isDisabled" @click="toggleFitMode">{{ toggleLabel }}</LinkWithIcon>
        instead of {{ currentNoun }}, or manually crop to {{ META_FEED_MIN_ASPECT_RATIO_LABEL }} in the Image Editor.
      </div>
    </template>
  </NoteWithIcon>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import { SocialImageFitMode, SocialImageFitSettings, SocialPostPublishType } from "@contracts/shareStop";
import { AlbumMediaPostType, anyImageNeedsMetaFeedAspectAdjustment, isReelPostType, isStoryPostType, META_FEED_MIN_ASPECT_RATIO_LABEL, resolveSocialImageFitMode } from "@logic/AtlasSchedulingValidation";

const props = withDefaults(
  defineProps<{
    assetIds: string[];
    postType?: AlbumMediaPostType | null;
    imageFit?: SocialImageFitSettings | null;
    calendarImageFit?: SocialImageFitSettings | null;
    isDisabled?: boolean;
  }>(),
  {
    assetIds: () => [],
    postType: SocialPostPublishType.Post,
    imageFit: null,
    calendarImageFit: null,
    isDisabled: false,
  }
);

const emit = defineEmits<{
  (e: "update:imageFit", value: SocialImageFitSettings): void;
}>();

const resolvedFitMode = computed(() => resolveSocialImageFitMode(props.imageFit, props.calendarImageFit));
const isPadMode = computed(() => resolvedFitMode.value === SocialImageFitMode.Pad);
const currentActionVerb = computed(() => (isPadMode.value ? "padded" : "cropped"));
const currentNoun = computed(() => (isPadMode.value ? "padding" : "cropping"));
const toggleLabel = computed(() => (isPadMode.value ? "use cropping" : "use padding"));
const isFeedPost = computed(() => !isReelPostType(props.postType ?? SocialPostPublishType.Post) && !isStoryPostType(props.postType ?? SocialPostPublishType.Post));

const hasOutOfRangeImage = ref(false);
const aspectCheckCache = new Map<string, Promise<boolean>>();
let aspectCheckVersion = 0;

function getAspectCheck(assetId: string): Promise<boolean> {
  const cachedCheck = aspectCheckCache.get(assetId);
  if (cachedCheck) return cachedCheck;
  const newCheck = anyImageNeedsMetaFeedAspectAdjustment([assetId]);
  aspectCheckCache.set(assetId, newCheck);
  return newCheck;
}

watch(
  [() => props.assetIds.join("|"), isFeedPost],
  async () => {
    const checkVersion = ++aspectCheckVersion;
    hasOutOfRangeImage.value = false;
    if (!isFeedPost.value || !props.assetIds.length) return;
    const results = await Promise.all(props.assetIds.map(getAspectCheck));
    if (checkVersion === aspectCheckVersion) {
      hasOutOfRangeImage.value = results.some(Boolean);
    }
  },
  { immediate: true }
);

const isVisible = computed(() => isFeedPost.value && hasOutOfRangeImage.value);

function toggleFitMode() {
  if (props.isDisabled) return;
  emit("update:imageFit", { mode: isPadMode.value ? SocialImageFitMode.Crop : SocialImageFitMode.Pad });
}
</script>

<style scoped lang="scss">
.meta-image-fit-notice {
  margin-top: 14px;
  width: fit-content;
  max-width: 100%;
  padding-right: 12px;
}

.meta-image-fit-notice__toggle {
  display: inline;
}
</style>
